import { useCallback, useEffect, useState } from "react";
import {
  GPS_BAD_ACCURACY_M,
  GPS_OK_ACCURACY_M,
  distanceToZone,
  isWithinBounds,
  outOfBoundsHint,
} from "../geo/bounds";
import { ALLOWED_NAME_CHARS } from "../geo/blocks";
import MapPane, { type MapPin } from "../components/MapPane";
import type { CaptureBlock, CaptureNotebook, GpsFix, GpsStatus } from "../types";

type Props = {
  notebook: CaptureNotebook;
  onUpdate: (next: CaptureNotebook) => void;
};

function prettyLabel(name: string): string {
  return name
    .split(/[-_]/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

export default function CapturePage({ notebook, onUpdate }: Props) {
  const captures = notebook.blocks;
  const capturedNames = new Set(captures.map((b) => b.name));

  const [gps, setGps] = useState<GpsFix | null>(null);
  const [gpsStatus, setGpsStatus] = useState<GpsStatus>("searching");
  const [pin, setPin] = useState<MapPin>(null);
  const [targetName, setTargetName] = useState<string | null>(() => {
    const first = notebook.blockNames.find((n) => !capturedNames.has(n));
    return first ?? notebook.blockNames[0] ?? null;
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [newBlockName, setNewBlockName] = useState("");

  // Live GPS fix.
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setGpsStatus("unsupported");
      return;
    }
    setGpsStatus("searching");
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const accuracy = pos.coords.accuracy ?? null;
        setGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: accuracy === null ? undefined : accuracy,
          timestamp: pos.timestamp,
        });
        setGpsStatus(
          accuracy === null || accuracy <= GPS_BAD_ACCURACY_M
            ? accuracy === null || accuracy <= GPS_OK_ACCURACY_M
              ? "good"
              : "poor"
            : "poor",
        );
      },
      (err) => {
        setGpsStatus(err.code === err.PERMISSION_DENIED ? "denied" : "error");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const gpsInBounds = gps ? isWithinBounds(gps.latitude, gps.longitude) : false;
  const gpsGood = gpsStatus === "good" && gpsInBounds;
  const pinReady = pin !== null && isWithinBounds(pin.latitude, pin.longitude);
  const remaining = notebook.blockNames.filter((n) => !capturedNames.has(n));

  useEffect(() => {
    if (!targetName || capturedNames.has(targetName)) {
      const next = remaining[0] ?? notebook.blockNames[0] ?? null;
      if (next !== targetName) setTargetName(next);
    }
  }, [notebook.blocks, capturedNames, targetName, remaining]);

  const addBlock = useCallback(() => {
    const cleaned = newBlockName.trim().replace(ALLOWED_NAME_CHARS, "-").trim();
    const name = cleaned.replace(/^[^a-zA-Z0-9]+/, "").replace(/[^a-zA-Z0-9._-]+$/g, "");
    if (!name) {
      setNotice("Enter a block name to add.");
      return;
    }
    if (notebook.blockNames.includes(name)) {
      setNotice(`"${name}" is already in the list.`);
      return;
    }
    onUpdate({
      ...notebook,
      blockNames: [...notebook.blockNames, name],
    });
    setNewBlockName("");
    setTargetName(name);
    setNotice(`Added "${name}" — capture it now.`);
  }, [newBlockName, notebook, onUpdate]);

  const removeBlock = useCallback(
    (name: string) => {
      const next: CaptureNotebook = {
        ...notebook,
        blockNames: notebook.blockNames.filter((n) => n !== name),
        blocks: notebook.blocks.filter((b) => b.name !== name),
      };
      onUpdate(next);
      if (targetName === name) {
        setTargetName(next.blockNames[0] ?? null);
      }
    },
    [notebook, onUpdate, targetName],
  );

  const moveBlock = useCallback(
    (name: string, dir: -1 | 1) => {
      const idx = notebook.blockNames.indexOf(name);
      const to = idx + dir;
      if (idx < 0 || to < 0 || to >= notebook.blockNames.length) return;
      const names = [...notebook.blockNames];
      const [item] = names.splice(idx, 1);
      names.splice(to, 0, item);
      onUpdate({ ...notebook, blockNames: names });
    },
    [notebook, onUpdate],
  );

  const capture = useCallback(
    (source: "gps" | "map", latitude: number, longitude: number, accuracy?: number) => {
      if (!targetName) {
        setNotice("Pick a block first.");
        return;
      }
      const entry: CaptureBlock = {
        name: targetName,
        label: prettyLabel(targetName),
        latitude,
        longitude,
        accuracy,
        source,
        capturedAt: new Date().toISOString(),
      };
      const blocks = [...notebook.blocks.filter((b) => b.name !== targetName), entry];
      onUpdate({ ...notebook, blocks });
      setPin(null);
      setNotice(
        `Captured ${targetName}${accuracy !== undefined ? ` (${Math.round(accuracy)} m)` : ""} — ${remaining.length - 1 <= 0 ? "all done 🎉" : "next: " + remaining.filter((n) => n !== targetName)[0]}`,
      );
    },
    [notebook, onUpdate, targetName, remaining],
  );

  const handleMapPick = useCallback(
    (latitude: number, longitude: number) => {
      if (!isWithinBounds(latitude, longitude)) {
        setNotice(`Outside capture zone — ${outOfBoundsHint(latitude, longitude)}.`);
        setPin(null);
        return;
      }
      setPin({ latitude, longitude });
      setNotice("Pin placed — hit “Use pin” to save it.");
    },
    [],
  );

  const gpsPillClass = `pill pill-${gpsStatus}`;
  const gpsPillText =
    gpsStatus === "good"
      ? `good fix${gps?.accuracy !== undefined ? ` · ${Math.round(gps.accuracy)} m` : ""}`
      : gpsStatus === "poor"
        ? `weak fix${gps?.accuracy !== undefined ? ` · ${Math.round(gps.accuracy)} m` : ""}`
        : gpsStatus === "searching"
          ? "finding signal…"
          : gpsStatus === "denied"
            ? "location denied"
            : gpsStatus === "unsupported"
              ? "no GPS support"
              : "GPS error";

  return (
    <div className="capture">
      <div className="capture-main">
        {(gps && !gpsInBounds) || (pin && !isWithinBounds(pin.latitude, pin.longitude)) ? (
          <div className="zone-warn">
            {gps && !gpsInBounds
              ? `Outside capture zone (${Math.round(distanceToZone(gps.latitude, gps.longitude))} m away) — ${outOfBoundsHint(gps.latitude, gps.longitude)}.`
              : "Pin is outside the capture zone — it will not be recorded."}
          </div>
        ) : null}
        <MapPane blocks={captures} gps={gps} pin={pin} onPick={handleMapPick} />
      </div>

      <aside className="side">
        <section className="card">
          <div className="card-head">
            <h2>{targetName ? `Capture · ${prettyLabel(targetName)}` : "Capture"}</h2>
            {targetName && (
              <span className="pill pill-neutral">{capturedNames.has(targetName) ? "already captured" : "next"}</span>
            )}
          </div>

          <div className="gps-row">
            <span className={gpsPillClass}>{gpsPillText}</span>
            <span className="muted">
              {gps && gpsInBounds
                ? `at ${gps.latitude.toFixed(5)}, ${gps.longitude.toFixed(5)}`
                : gps && !gpsInBounds
                  ? "outside zone"
                  : "—"}
            </span>
          </div>

          <button
            className="btn btn-primary"
            disabled={!gpsGood}
            onClick={() => gps && capture("gps", gps.latitude, gps.longitude, gps.accuracy)}
          >
            {gpsGood
              ? `Capture GPS${gps?.accuracy !== undefined ? ` (${Math.round(gps.accuracy)} m)` : ""}`
              : gpsStatus === "poor" && gps
                ? "Wait for a better fix"
                : gpsStatus === "searching"
                  ? "Finding signal…"
                  : "Capture GPS"}
          </button>

          <button className="btn" disabled={!pinReady} onClick={() => pin && capture("map", pin.latitude, pin.longitude)}>
            Use map pin
          </button>

          <p className="hint">
            {gpsStatus === "denied"
              ? "Enable location access for this site in your browser, then reload."
              : gpsStatus === "unsupported"
                ? "This device has no geolocation — use the map pin instead."
                : "Tap the map to place a pin, or stand at the block and capture the live GPS fix. Only coordinates inside the green zone are accepted."}
          </p>

          {notice && <div className="notice">{notice}</div>}
        </section>

        <section className="card">
          <div className="card-head">
            <h2>Blocks</h2>
            <span className="pill pill-neutral">
              {captures.length}/{notebook.blockNames.length} captured
            </span>
          </div>

          <div className="add-block">
            <input
              value={newBlockName}
              onChange={(e) => setNewBlockName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addBlock()}
              placeholder="add block, e.g. campus-block-h"
              aria-label="Add block"
            />
            <button className="btn btn-small" onClick={addBlock}>
              Add
            </button>
          </div>

          <ul className="block-list">
            {notebook.blockNames.map((name) => {
              const cap = captures.find((b) => b.name === name);
              const isTarget = targetName === name;
              return (
                <li key={name} className={isTarget ? "block-row block-row-active" : "block-row"} onClick={() => setTargetName(name)}>
                  <button
                    className="btn btn-ghost btn-tiny"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveBlock(name, -1);
                    }}
                    aria-label={`Move ${name} up`}
                  >
                    ↑
                  </button>
                  <button
                    className="btn btn-ghost btn-tiny"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveBlock(name, 1);
                    }}
                    aria-label={`Move ${name} down`}
                  >
                    ↓
                  </button>
                  <span className="block-name">{prettyLabel(name)}</span>
                  {cap ? (
                    <span className="pill pill-ok">
                      {cap.source === "gps" && cap.accuracy !== undefined
                        ? `${Math.round(cap.accuracy)} m`
                        : "pin"}
                    </span>
                  ) : (
                    <span className="pill pill-muted">—</span>
                  )}
                  <button
                    className="btn btn-ghost btn-tiny danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBlock(name);
                    }}
                    aria-label={`Remove ${name}`}
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </aside>
    </div>
  );
}